import React, { useEffect, useState } from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import { Grid } from '@nubank/nuds-web/components/Grid/Grid';
import styled, { css, keyframes } from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Button from '@nubank/nuds-web/components/Button/Button';
import { breakpointsMedia } from '@nubank/nuds-web/styles/breakpoints';

import ImagePreload from '@nubank/www-latam-commons/components/ImagePreload/ImagePreload';
import useWindowSize from '@nubank/www-latam-commons/utils/hooks/useWindowSize';

import FormBox from '../../../../components/FormBox/FormBox';

const BANK_HERO_IMAGE = {
  xs: 'benefits/doggo-bank-xs.jpg',
  lg: 'benefits/doggo-bank-lg.png',
};

const StyledTypography = styled(Typography)`
  background: ${props => props.gradient};
  /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  height: 100%;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const StyledBox = styled(Box)`
  animation: ${fadeIn} .6s linear;

  ${breakpointsMedia({
    lg: css`
      animation: none;
    `,
  })}
`;

function useWindowSizes(width, controlHeight) {
  const [windowSize, setWindowSize] = useState({
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        height: width <= controlHeight ? '60vh' : '100%',
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [width, controlHeight]);

  return windowSize;
}

const StyledImage = styled(Image)`
  border-radius: 15px;
  max-height: 60vh;

  ${breakpointsMedia({
    md: css`
      max-height: 420px;
      margin: 0 auto;
    `,
  })}
`;

const StyledGrid = styled(Grid)`
  max-width: 1580px;
  margin: 0 auto;
`;

const Hero = () => {
  const { width, height } = useWindowSize();
  const minHeight = 425;
  const sizes = useWindowSizes(width, minHeight);

  const scrollDown = () => {
    window.scrollTo({
      top: height - 80,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <ImagePreload srcSet={BANK_HERO_IMAGE} />
      <StyledBox
        tag="section"
        paddingTop={{ xs: '80px', lg: '140px' }}
        paddingHorizontal={{
          xs: '6x', md: '48px', lg: '6vw',
        }}
        height="100%"
        marginBottom={{ xs: '0', md: '40px' }}
      >
        <StyledGrid
          paddingRight="0"
          paddingLeft="0"
          gridTemplateRows="1fr auto"
        >
          <Grid.Child
            gridRow="1"
            gridColumn={{
              xs: '1 / span 4', md: ' 1 / span 6', lg: '1 / span 12', xl: '2 / span 10',
            }}
            justifySelf="center"
          >
            <StyledTypography
              variant="heading2"
              whiteSpace={{ xs: 'auto', md: 'pre-line', lg: 'normal' }}
              gradient="linear-gradient(99.92deg, #9F2DEB 18.57%, #6200A3 71.77%)"
              textAlign="center"
              marginBottom={{ xs: '60px', lg: '18x' }}
              width={{ xs: '14ch', md: '100%' }}
              intlKey="BENEFITS.HERO.TITLE_BANK"
            />
          </Grid.Child>

          <Grid.Child
            gridRow="2"
            gridColumn={{
              xs: '1 / span 4', md: ' 1 / span 6', lg: '1 / span 12',
            }}
          >
            <Box position="relative">
              <Box
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                display="flex"
                alignItems={{ xs: 'flex-end', md: 'center' }}
                justifyContent={{ xs: 'center', md: 'flex-start' }}
                paddingLeft={{ xs: '0', md: '48px' }}
                paddingBottom={{ xs: '6x', md: '0' }}
              >
                <FormBox
                  buttonLabel="HOME.HERO.BUTTON_LABEL.MULTIPRODUCT"
                  ctaHide
                />
              </Box>

              <StyledImage
                srcSet={BANK_HERO_IMAGE}
                alt="Dos personas felices comiendo un taco"
                objectFit="cover"
                height={{
                  xs: sizes.height,
                  md: '60vh',
                  lg: '420px',
                }}
                minHeight="100%"
              />
            </Box>
          </Grid.Child>

          <Grid.Child
            gridRow="2"
            gridColumn={{ xs: '1 / span 4', md: ' 1 / span 6', lg: '1 / span 12' }}
            alignSelf={{ xs: 'end' }}
            justifySelf={{ xs: 'center' }}
          >
            <Box
              marginTop={{ xs: '-6x' }}
              position="relative"
              zIndex="1"
              marginBottom="-28px"
              display={{ xs: 'none', md: 'block' }}
            >
              <Button
                variant="contained"
                iconProps={{ name: 'arrow-down', title: 'Bajar a siguiente sección' }}
                onClick={scrollDown}
              />
            </Box>
          </Grid.Child>
        </StyledGrid>
      </StyledBox>
    </>
  );
};

export default Hero;
